import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { Button, Card, Input, Select, Spinner, Empty, fmtBRL } from "@/components/ui/Primitives";
import { Tabs } from "@/components/ui/Tabs";
import { api, formatErr, fmtDate } from "@/lib/api";
import { Plus, X, ArrowUpRight, ArrowDownRight, Trash2, AlertTriangle, RefreshCw, Users, Package, Boxes } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  ComposedChart, Line,
} from "recharts";

const empty = { tipo: "saida", descricao: "", valor: "", data: new Date().toISOString().slice(0, 10), categoria: "outros", observacoes: "" };

const ORIGENS_META = {
  AR: { label: "Contas a Receber", tone: "text-emerald-700", icon: ArrowUpRight },
  AP: { label: "Contas a Pagar", tone: "text-danger", icon: ArrowDownRight },
  recorrente: { label: "Recorrentes", tone: "text-brand-500", icon: RefreshCw },
  folha: { label: "Folha (projetada)", tone: "text-warn", icon: Users },
  estoque: { label: "Estoque (projeção)", tone: "text-emerald-600", icon: Package },
};

export default function Cashflow() {
  const [tab, setTab] = useState("realizado");
  const [data, setData] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(90);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Toggles das fontes de projeção
  const [incRecurring, setIncRecurring] = useState(true);
  const [incPayroll, setIncPayroll] = useState(true);
  const [incStock, setIncStock] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [rz, pv] = await Promise.all([
        api.get("/cashflow", { params: { days } }),
        api.get("/cashflow/forecast", {
          params: {
            days: 60,
            include_recurring: incRecurring,
            include_payroll: incPayroll,
            include_stock: incStock,
            chart_months: 3,
          },
        }),
      ]);
      setData(rz.data);
      setForecast(pv.data);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days, incRecurring, incPayroll, incStock]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/cashflow/entry", { ...form, valor: Number(form.valor) || 0 });
      setOpen(false); setForm(empty); await load();
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  const onDelete = async (id, origem) => {
    if (origem !== "manual") return;
    if (!window.confirm("Excluir lançamento?")) return;
    try { await api.delete(`/cashflow/entry/${id}`); await load(); }
    catch (e) { alert(formatErr(e)); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const current = tab === "realizado" ? data : forecast;
  const chartData = useMemo(() => (current?.monthly || []), [current]);

  return (
    <Layout
      title="Fluxo de Caixa"
      subtitle={tab === "realizado" ? "Movimentação efetiva (pagamentos + lançamentos avulsos)" : "Recebimentos e pagamentos previstos por vencimento (próximos 60 dias)"}
      actions={
        <div className="flex gap-2">
          {tab === "realizado" && (
            <Select value={days} onChange={(e) => setDays(Number(e.target.value))} className="w-32" data-testid="cashflow-period">
              <option value={30}>30 dias</option>
              <option value={90}>90 dias</option>
              <option value={180}>180 dias</option>
              <option value={365}>365 dias</option>
            </Select>
          )}
          <Button onClick={() => setOpen(true)} data-testid="new-cashflow-button"><Plus size={16} /> Lançamento</Button>
        </div>
      }
    >
      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { value: "realizado", label: "Realizado" },
          { value: "previsto", label: "Previsto (mensal)" },
          { value: "diario", label: "Diário (fluxo de caixa)" },
        ]}
      />

      {err && <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md my-4">{err}</div>}
      {loading ? (
        <div className="flex justify-center py-20"><Spinner size={28} /></div>
      ) : tab === "diario" ? (
        <DailyView forecast={forecast} />
      ) : (
        <div className="mt-6">
          {/* Toggles das fontes de projeção */}
          {tab === "previsto" && (
            <Card className="p-4 mb-4">
              <div className="flex items-center gap-6 flex-wrap">
                <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Incluir na projeção:</div>
                <SourceToggle
                  checked={incRecurring}
                  onChange={setIncRecurring}
                  icon={RefreshCw}
                  label="Recorrentes futuras"
                  testid="toggle-recurring"
                />
                <SourceToggle
                  checked={incPayroll}
                  onChange={setIncPayroll}
                  icon={Users}
                  label="Folha estimada"
                  testid="toggle-payroll"
                />
                <SourceToggle
                  checked={incStock}
                  onChange={setIncStock}
                  icon={Package}
                  label="Estoque (30d)"
                  testid="toggle-stock"
                />
              </div>
            </Card>
          )}

          {/* Aviso vencidos (só no previsto) */}
          {tab === "previsto" && (forecast?.totals?.vencidas_entrada > 0 || forecast?.totals?.vencidas_saida > 0) && (
            <div className="mb-4 p-4 rounded-lg bg-amber-50 border border-amber-300 flex items-start gap-3">
              <AlertTriangle size={18} className="text-warn flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <div className="font-semibold text-amber-900">Existem valores vencidos:</div>
                <div className="text-xs text-amber-800 mt-1">
                  A receber vencidas: <strong className="font-mono">{fmtBRL(forecast?.totals?.vencidas_entrada || 0)}</strong>
                  {" · "}A pagar vencidas: <strong className="font-mono">{fmtBRL(forecast?.totals?.vencidas_saida || 0)}</strong>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="p-5 rounded-lg shadow-sm border bg-white border-emerald-200">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.1em] text-ok"><ArrowUpRight size={14} /> {tab === "realizado" ? "Entradas" : "A receber"}</div>
              <div className="font-mono text-2xl font-semibold text-ok mt-1.5" data-testid="cashflow-entradas">{fmtBRL(current?.totals?.entradas || 0)}</div>
            </div>
            <div className="p-5 rounded-lg shadow-sm border bg-white border-red-200">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.1em] text-danger"><ArrowDownRight size={14} /> {tab === "realizado" ? "Saídas" : "A pagar"}</div>
              <div className="font-mono text-2xl font-semibold text-danger mt-1.5" data-testid="cashflow-saidas">{fmtBRL(current?.totals?.saidas || 0)}</div>
            </div>
            <div className={`p-5 rounded-lg shadow-sm border ${(current?.totals?.saldo || 0) < 0 ? "bg-danger border-danger" : "bg-brand-500 border-brand-500"}`}>
              <div className={`text-xs font-bold uppercase tracking-[0.1em] ${(current?.totals?.saldo || 0) < 0 ? "text-red-100" : "text-emerald-100"}`}>{tab === "realizado" ? "Saldo" : "Saldo previsto"}</div>
              <div className="font-mono text-2xl font-semibold text-white mt-1.5" data-testid="cashflow-saldo">{fmtBRL(current?.totals?.saldo || 0)}</div>
            </div>
          </div>

          <Card className="p-6 mb-6">
            <h3 className="font-heading font-bold text-ink-900 mb-4">Mês a mês {tab === "previsto" && <span className="text-xs font-normal text-ink-500">(por vencimento)</span>}</h3>
            {chartData.length === 0 ? (
              <div className="h-40 flex items-center justify-center text-ink-300 text-sm">Sem {tab === "realizado" ? "movimentação" : "vencimentos"} no período</div>
            ) : (
              <div className="h-72 min-h-[280px]">
                <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                    <XAxis dataKey="month" stroke="#57534E" tick={{ fontSize: 11 }} />
                    <YAxis stroke="#57534E" tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E7E5E4", fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="entradas" fill="#15803D" name={tab === "realizado" ? "Entradas" : "A receber"} radius={[4,4,0,0]} />
                    <Bar dataKey="saidas" fill="#DC2626" name={tab === "realizado" ? "Saídas" : "A pagar"} radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>

          {/* Projeção 12 meses com saldo acumulado (só no Previsto) */}
          {tab === "previsto" && forecast?.chart_12m?.length > 0 && (
            <Card className="p-6 mb-6">
              <h3 className="font-heading font-bold text-ink-900 mb-1">Projeção 60 dias</h3>
              <p className="text-xs text-ink-500 mb-4">Barras = entradas × saídas mensais · Linha = saldo acumulado projetado</p>
              <div className="h-80 min-h-[300px]">
                <ResponsiveContainer width="100%" height="100%" minHeight={300}>
                  <ComposedChart data={forecast.chart_12m}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                    <XAxis dataKey="month" stroke="#57534E" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="left" stroke="#57534E" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="right" orientation="right" stroke="#1E3F33" tick={{ fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{ borderRadius: 8, border: "1px solid #E7E5E4", fontSize: 12 }}
                      formatter={(v) => fmtBRL(v)}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar yAxisId="left" dataKey="entradas" fill="#15803D" name="Entradas" radius={[3,3,0,0]} />
                    <Bar yAxisId="left" dataKey="saidas" fill="#DC2626" name="Saídas" radius={[3,3,0,0]} />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="saldo_acumulado"
                      stroke="#1E3F33"
                      strokeWidth={3}
                      dot={{ r: 4, fill: "#C9A24E", stroke: "#1E3F33", strokeWidth: 2 }}
                      name="Saldo acumulado"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </Card>
          )}

          {/* Breakdown por origem (só no Previsto) */}
          {tab === "previsto" && (forecast?.breakdown?.length || 0) > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="forecast-breakdown">
              {forecast.breakdown.map((b) => {
                const meta = ORIGENS_META[b.origem] || { label: b.origem, tone: "text-ink-700", icon: Boxes };
                const Icon = meta.icon;
                return (
                  <div key={b.origem} className="p-4 rounded-lg border bg-white border-ink-200">
                    <div className={`flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.1em] ${meta.tone}`}>
                      <Icon size={12} /> {meta.label}
                    </div>
                    <div className="mt-2 text-xs text-ink-500">
                      <span className="text-emerald-700 font-mono">+{fmtBRL(b.entradas)}</span>
                      {" · "}
                      <span className="text-danger font-mono">−{fmtBRL(b.saidas)}</span>
                    </div>
                    <div className={`font-mono text-sm font-semibold mt-1 ${b.saldo < 0 ? "text-danger" : "text-emerald-700"}`}>
                      {fmtBRL(b.saldo)}
                    </div>
                    <div className="text-[10px] text-ink-500 mt-0.5">{b.n} lançamento{b.n !== 1 ? "s" : ""}</div>
                  </div>
                );
              })}
            </div>
          )}

          {(current?.entries || []).length === 0 ? (
            <Card><Empty title={tab === "realizado" ? "Sem movimentações no período" : "Nenhum vencimento previsto"} hint={tab === "realizado" ? "Registre pagamentos ou crie um lançamento avulso." : "Ao finalizar saídas e beneficiamentos, as contas aparecerão aqui."} /></Card>
          ) : (
            <Card className="overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 border-b border-ink-200">
                  <tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-500">
                    <th className="px-4 py-3">{tab === "realizado" ? "Data" : "Vencimento"}</th>
                    <th className="px-4 py-3">Tipo</th>
                    <th className="px-4 py-3">Descrição</th>
                    <th className="px-4 py-3">{tab === "realizado" ? "Categoria" : "Cliente / Fornecedor"}</th>
                    <th className="px-4 py-3">{tab === "realizado" ? "Método" : "Categoria"}</th>
                    <th className="px-4 py-3 text-right">Valor</th>
                    <th className="px-4 py-3">Origem</th>
                    {tab === "realizado" && <th className="px-4 py-3 w-1">Ações</th>}
                  </tr>
                </thead>
                <tbody>
                  {(current?.entries || []).map((e) => (
                    <tr key={e.id} className={`border-b border-ink-200 last:border-0 hover:bg-stone-50 transition-colors ${e.vencida ? "bg-amber-50/40" : ""}`}>
                      <td className="px-4 py-3 font-mono">
                        {fmtDate(e.date)}
                        {e.vencida && <div className="text-[10px] font-bold uppercase text-warn mt-0.5">vencida</div>}
                      </td>
                      <td className="px-4 py-3">
                        {e.tipo === "entrada"
                          ? <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase text-emerald-700"><ArrowUpRight size={12} /> Entrada</span>
                          : <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase text-danger"><ArrowDownRight size={12} /> Saída</span>}
                      </td>
                      <td className="px-4 py-3 text-ink-900">{e.descricao}</td>
                      <td className="px-4 py-3 capitalize text-ink-700">{tab === "realizado" ? e.categoria : (e.cliente_fornecedor || "—")}</td>
                      <td className="px-4 py-3 capitalize text-ink-700">{tab === "realizado" ? (e.metodo || "—") : e.categoria}</td>
                      <td className={`px-4 py-3 font-mono text-right font-semibold ${e.tipo === "entrada" ? "text-emerald-700" : "text-danger"}`}>
                        {e.tipo === "entrada" ? "+ " : "− "}{fmtBRL(e.valor)}
                      </td>
                      <td className="px-4 py-3 text-xs text-ink-500 capitalize">{e.origem || (e.linked_to === "accounts_receivable" ? "AR" : "AP")}</td>
                      {tab === "realizado" && (
                        <td className="px-4 py-3">
                          {e.origem === "manual" && (
                            <button onClick={() => onDelete(e.id, e.origem)} className="p-1.5 rounded-md hover:bg-red-100 text-danger" title="Excluir"><Trash2 size={16} /></button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-stone-900/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md animate-fade-in">
            <div className="flex items-center justify-between px-6 py-4 border-b border-ink-200">
              <h3 className="font-heading font-bold text-ink-900">Lançamento avulso</h3>
              <button onClick={() => setOpen(false)} className="text-ink-500"><X size={18} /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <Select label="Tipo" value={form.tipo} onChange={set("tipo")} data-testid="cashflow-tipo">
                <option value="entrada">Entrada</option>
                <option value="saida">Saída</option>
              </Select>
              <Input label="Descrição" required value={form.descricao} onChange={set("descricao")} data-testid="cashflow-desc" />
              <div className="grid grid-cols-2 gap-3">
                <Input label="Valor (R$)" type="number" step="0.01" required value={form.valor} onChange={set("valor")} data-testid="cashflow-valor" />
                <Input label="Data" type="date" required value={form.data} onChange={set("data")} />
              </div>
              <Select label="Categoria" value={form.categoria} onChange={set("categoria")}>
                <option value="outros">Outros</option>
                <option value="frete">Frete</option>
                <option value="sacaria">Sacaria</option>
                <option value="energia">Energia</option>
                <option value="aluguel">Aluguel</option>
                <option value="folha">Folha</option>
                <option value="salario_adm">Salários ADM</option>
                <option value="venda">Venda</option>
                <option value="impostos">Impostos</option>
                <option value="manutencao">Manutenção</option>
                <option value="materiais_escritorio">Materiais de Escritório</option>
                <option value="materiais_limpeza">Materiais de Limpeza</option>
                <option value="taxas_bancarias">Taxas Bancárias</option>
                <option value="cesta_basica">Cesta Básica</option>
              </Select>
              <Input label="Observações" value={form.observacoes} onChange={set("observacoes")} />
              {err && <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>}
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button type="submit" disabled={busy} data-testid="cashflow-submit">{busy ? <Spinner size={16} /> : "Salvar"}</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}

function SourceToggle({ checked, onChange, icon: Icon, label, testid }) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid={testid}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 accent-brand-500 cursor-pointer"
      />
      <Icon size={14} className={checked ? "text-brand-500" : "text-ink-500"} />
      <span className={`text-sm ${checked ? "text-ink-900 font-medium" : "text-ink-500"}`}>{label}</span>
    </label>
  );
}

function DailyView({ forecast }) {
  const daily = forecast?.daily || [];
  const [company, setCompany] = useState(null);
  // Saldo inicial em caixa (persistido no navegador)
  const [saldoInicial, setSaldoInicial] = useState(() => {
    try {
      const stored = localStorage.getItem("cashflow_saldo_inicial");
      return stored !== null ? Number(stored) : null; // null = ainda não escolheu
    } catch { return null; }
  });

  // Carrega saldos da Empresa
  useEffect(() => {
    api.get("/company").then((r) => {
      setCompany(r.data);
      // Se usuário nunca configurou o saldo inicial, usa o total da Empresa por padrão
      if (saldoInicial === null) {
        const total = Number(r.data.saldo_sicredi || 0) + Number(r.data.saldo_banco_brasil || 0) +
                      Number(r.data.saldo_safra || 0) + Number(r.data.saldo_cheques || 0) +
                      Number(r.data.saldo_dinheiro || 0);
        setSaldoInicial(total);
      }
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (saldoInicial !== null) {
      try { localStorage.setItem("cashflow_saldo_inicial", String(saldoInicial)); } catch { /* ignore */ }
    }
  }, [saldoInicial]);

  const saldoEmpresa = company ? (
    Number(company.saldo_sicredi || 0) + Number(company.saldo_banco_brasil || 0) +
    Number(company.saldo_safra || 0) + Number(company.saldo_cheques || 0) +
    Number(company.saldo_dinheiro || 0)
  ) : null;
  const saldoInicialValor = saldoInicial ?? 0;

  const [expanded, setExpanded] = useState(null); // date expanded

  // Calcula saldo acumulado dia-a-dia + detecta primeiro dia sem saldo
  const rows = useMemo(() => {
    let acc = Number(saldoInicialValor) || 0;
    return daily.map((d) => {
      acc += (d.entradas - d.saidas);
      return { ...d, saldo_acumulado: Math.round(acc * 100) / 100 };
    });
  }, [daily, saldoInicialValor]);
  const primeiroNegativo = rows.find((r) => r.saldo_acumulado < 0);
  const menorSaldo = rows.reduce((min, r) => (r.saldo_acumulado < min.saldo_acumulado ? r : min), { saldo_acumulado: Infinity, date: null });

  if (!daily.length) {
    return <div className="mt-6"><Empty title="Sem projeção" subtitle="Nenhum recebimento ou pagamento previsto no período." /></div>;
  }

  return (
    <div className="mt-6">
      {/* Card de saldo inicial ocultado — o cálculo continua usando o valor herdado da Empresa/localStorage */}

      {/* Alertas */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Card className={`p-5 ${primeiroNegativo ? "bg-red-50 border-red-300" : "bg-emerald-50 border-emerald-200"}`}>
          <div className={`flex items-center gap-2 text-xs font-bold uppercase tracking-[0.1em] ${primeiroNegativo ? "text-danger" : "text-ok"}`}>
            <AlertTriangle size={14} /> {primeiroNegativo ? "Atenção: caixa fica no vermelho" : "Fluxo saudável no período"}
          </div>
          {primeiroNegativo ? (
            <>
              <div className="font-mono text-xl font-semibold text-danger mt-1.5" data-testid="cashflow-primeiro-negativo-data">
                {fmtDate(primeiroNegativo.date)}
              </div>
              <div className="text-xs text-ink-700 mt-1">
                No dia {fmtDate(primeiroNegativo.date)} o saldo acumulado fica em{" "}
                <span className="font-mono font-bold text-danger">{fmtBRL(primeiroNegativo.saldo_acumulado)}</span>
              </div>
            </>
          ) : (
            <div className="text-xs text-ink-700 mt-1.5">Todos os dias projetados fecham com saldo positivo.</div>
          )}
        </Card>

        <Card className="p-5 bg-white border-ink-200">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.1em] text-ink-500">
            Menor saldo previsto
          </div>
          <div className={`font-mono text-xl font-semibold mt-1.5 ${menorSaldo.saldo_acumulado < 0 ? "text-danger" : "text-ink-900"}`} data-testid="cashflow-menor-saldo">
            {fmtBRL(menorSaldo.saldo_acumulado === Infinity ? 0 : menorSaldo.saldo_acumulado)}
          </div>
          <div className="text-xs text-ink-500 mt-1">
            {menorSaldo.date ? `em ${fmtDate(menorSaldo.date)}` : "—"}
          </div>
        </Card>
      </div>

      {/* Tabela diária */}
      <Card className="overflow-hidden">
        <div className="px-5 py-4 border-b border-tertiary flex items-center justify-between">
          <div>
            <div className="font-bold text-ink-900 text-sm uppercase tracking-wider">Fluxo Diário</div>
            <div className="text-xs text-ink-500 mt-0.5">{rows.length} dia(s) com movimentação • clique numa linha pra ver os itens</div>
          </div>
        </div>
        <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="cashflow-daily-table">
          <thead className="bg-stone-50 border-b border-tertiary sticky top-0">
            <tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-500">
              <th className="px-4 py-3">Data</th>
              <th className="px-3 py-3 text-right">Entradas</th>
              <th className="px-3 py-3 text-right">Saídas</th>
              <th className="px-3 py-3 text-right">Saldo do dia</th>
              <th className="px-3 py-3 text-right">Saldo Acumulado</th>
              <th className="px-3 py-3 text-right">Itens</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const neg = r.saldo_acumulado < 0;
              const isFirst = primeiroNegativo && r.date === primeiroNegativo.date;
              return (
                <>
                  <tr
                    key={r.date}
                    onClick={() => setExpanded(expanded === r.date ? null : r.date)}
                    className={`border-b border-tertiary cursor-pointer transition-colors ${
                      neg ? "bg-red-50 hover:bg-red-100" : "hover:bg-stone-50"
                    } ${isFirst ? "ring-2 ring-danger ring-inset" : ""}`}
                    data-testid={`cashflow-day-${r.date}`}
                  >
                    <td className="px-4 py-2.5 font-mono whitespace-nowrap">
                      {fmtDate(r.date)}
                      {isFirst && <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-danger text-white text-[10px] font-bold uppercase"><AlertTriangle size={10} /> falta caixa</span>}
                    </td>
                    <td className="px-3 py-2.5 font-mono text-right text-ok">{r.entradas > 0 ? fmtBRL(r.entradas) : "—"}</td>
                    <td className="px-3 py-2.5 font-mono text-right text-danger">{r.saidas > 0 ? fmtBRL(r.saidas) : "—"}</td>
                    <td className={`px-3 py-2.5 font-mono text-right font-semibold ${r.saldo_dia >= 0 ? "text-ok" : "text-danger"}`}>{fmtBRL(r.saldo_dia)}</td>
                    <td className={`px-3 py-2.5 font-mono text-right font-bold ${neg ? "text-danger" : "text-ink-900"}`}>{fmtBRL(r.saldo_acumulado)}</td>
                    <td className="px-3 py-2.5 text-right text-xs text-ink-500">{r.n_items}</td>
                  </tr>
                  {expanded === r.date && (
                    <tr key={`${r.date}-detail`} className="bg-stone-50/50">
                      <td colSpan={6} className="px-4 py-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
                          {r.items.map((it, i) => (
                            <div key={i} className="flex items-center justify-between text-xs border-b border-tertiary py-1">
                              <div className="flex items-center gap-2 truncate">
                                {it.tipo === "entrada"
                                  ? <ArrowUpRight size={12} className="text-ok flex-shrink-0" />
                                  : <ArrowDownRight size={12} className="text-danger flex-shrink-0" />}
                                <span className="truncate text-ink-900">{it.descricao}</span>
                                {it.vencida && <span className="text-[10px] text-danger font-bold uppercase">venc.</span>}
                              </div>
                              <span className={`font-mono font-semibold ml-3 whitespace-nowrap ${it.tipo === "entrada" ? "text-ok" : "text-danger"}`}>
                                {it.tipo === "saida" ? "− " : "+ "}{fmtBRL(it.valor)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
        </div>
      </Card>
    </div>
  );
}
